import { useMemo, useState } from 'react';
import { SearchIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type { WorkspaceSummary } from '@/lib/api/generated/model';

import {
  someNames,
  workspaceImpact,
  type WorkspaceImpact,
} from '../commitCoverage';
import {
  usePeopleDirectory,
  useSetForgePolicy,
} from '../hooks/useLinkedAccounts';
import {
  LINKED_ACCOUNTS_COPY,
  POLICY_RAIL_COPY as WORDS,
} from '../linkedAccountsCopy';

/**
 * Require personal accounts in one more workspace (spec 165 US2). Every
 * workspace that does not yet is listed with who it would stop, so the cost is
 * read before one is picked; picking one says who, by name, before anything
 * is saved.
 */
export function RequirePolicySheet({
  open,
  onClose,
  workspaces,
  requiredIds,
}: {
  open: boolean;
  onClose: () => void;
  workspaces: readonly WorkspaceSummary[];
  requiredIds: readonly string[];
}) {
  const directory = usePeopleDirectory();
  const setPolicy = useSetForgePolicy();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const data = directory.data;
    return (
      workspaces
        .filter((workspace) => !requiredIds.includes(workspace.id))
        .map((workspace) => ({
          workspace,
          impact: data
            ? workspaceImpact(workspace.id, data.items, data.hosts)
            : null,
        }))
        // Those that connect to a forge first: there, requiring changes something.
        .sort(
          (a, b) =>
            Number((b.impact?.hosts.length ?? 0) > 0) -
              Number((a.impact?.hosts.length ?? 0) > 0) ||
            a.workspace.name.localeCompare(b.workspace.name),
        )
    );
  }, [workspaces, requiredIds, directory.data]);
  const needle = query.trim().toLowerCase();
  const shown = candidates.filter(({ workspace }) =>
    workspace.name.toLowerCase().includes(needle),
  );
  const choice = candidates.find(({ workspace }) => workspace.id === picked);
  const total = directory.data?.items.length ?? 0;

  const close = () => {
    if (setPolicy.isPending) return;
    setQuery('');
    setPicked(null);
    onClose();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{WORDS.sheetTitle}</SheetTitle>
          <SheetDescription>{WORDS.sheetDescription}</SheetDescription>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-3">
          {candidates.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {WORDS.nothingToRequire}
            </p>
          ) : (
            <>
              <label className="relative">
                <span className="sr-only">{WORDS.search}</span>
                <SearchIcon
                  aria-hidden="true"
                  className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
                />
                <Input
                  type="search"
                  value={query}
                  placeholder={WORDS.search}
                  onChange={(event) => setQuery(event.target.value)}
                  className="ps-8"
                />
              </label>
              {shown.length === 0 ? (
                <p className="text-muted-foreground text-sm">{WORDS.noMatch}</p>
              ) : (
                <ul
                  role="radiogroup"
                  aria-label={WORDS.sheetTitle}
                  className="border-border divide-border flex max-h-80 flex-col divide-y overflow-y-auto rounded-md border"
                >
                  {shown.map(({ workspace, impact }) => (
                    <li key={workspace.id}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={picked === workspace.id}
                        onClick={() => setPicked(workspace.id)}
                        className={`focus-visible:ring-ring flex min-h-11 w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm focus-visible:ring-3 focus-visible:outline-none focus-visible:ring-inset ${
                          picked === workspace.id
                            ? 'bg-primary/10 text-primary'
                            : 'text-foreground hover:bg-accent/50'
                        }`}
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {workspace.name}
                        </span>
                        <CostTag impact={impact} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {choice ? (
            <p
              role="status"
              className="border-border bg-muted text-foreground rounded-md border p-3 text-sm"
            >
              {preview(choice.workspace.name, choice.impact, total)}
            </p>
          ) : null}
          {setPolicy.isError ? (
            <p role="alert" className="text-state-danger text-sm">
              {LINKED_ACCOUNTS_COPY.policyFailed}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button variant="outline" onClick={close}>
            {WORDS.cancel}
          </Button>
          <Button
            disabled={!choice || setPolicy.isPending}
            onClick={() => {
              if (!choice) return;
              setPolicy.mutate(
                {
                  workspaceId: choice.workspace.id,
                  name: choice.workspace.name,
                  required: true,
                },
                {
                  onSuccess: () => {
                    setQuery('');
                    setPicked(null);
                    onClose();
                  },
                },
              );
            }}
          >
            {setPolicy.isPending ? WORDS.requiring : WORDS.require}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function CostTag({ impact }: { impact: WorkspaceImpact | null }) {
  if (!impact) return null;
  if (impact.hosts.length === 0) {
    return (
      <span className="text-muted-foreground shrink-0 text-xs">
        {WORDS.noForgeTag}
      </span>
    );
  }
  const count = impact.stopped.length;
  return (
    <span
      className={`shrink-0 text-xs tabular-nums ${
        count > 0 ? 'text-state-warning' : 'text-muted-foreground'
      }`}
    >
      {WORDS.wouldStop(count)}
    </span>
  );
}

function preview(
  name: string,
  impact: WorkspaceImpact | null,
  total: number,
): string {
  if (!impact || impact.hosts.length === 0) return WORDS.previewNoForge(name);
  if (impact.stopped.length === 0) return WORDS.previewNobody(name);
  return WORDS.previewStops(
    name,
    impact.stopped.length,
    total,
    someNames(impact.stopped),
  );
}
